import React from 'react';
import { AOO_API_HOST } from "../../../env";
import { postAction, postQueryAction } from "../WidgetActions";
import DisplayIcon from "../FailureIcon";
import { SubMenuItem } from './SubMenu';
import {useSWRConfig} from "swr";

// Worker menu items generator


const getWorkerDisplayName = (worker) => {
  return `(${worker.Initials}) ${worker.FullName.split(' ')[0] ?? worker.FullName}`
}

export const useWorkerItems = (
  workers: any[], 
  machineId: string,
  machineWorkers: any[],
  onToggleWorker: (workerName: string) => void,
  onClose: () => void
): SubMenuItem[] => {
  const { mutate } = useSWRConfig();
  if (!workers?.length) return [];
  
  // Helper function to check if a worker is assigned to the machine
  // Note: Only one worker can be assigned per machine
  const isWorkerAssigned = (worker) => {
    return machineWorkers.some(mw => {
      if (typeof mw === 'object') {
        const mwName = mw.FullName || mw.fullName || mw.Name || mw.name;
        return mwName === worker.FullName;
      }
      return false;
    });
  };
  
  // Create a copy and sort: selected worker first, then alphabetically
  const sortedWorkers = [...workers].sort((workerA, workerB) => {
    // First check if either worker is assigned to the machine
    const aIsSelected = isWorkerAssigned(workerA);
    const bIsSelected = isWorkerAssigned(workerB);
    
    if (aIsSelected && !bIsSelected) {
      return -1; // Selected worker comes first
    }
    if (!aIsSelected && bIsSelected) {
      return 1; // Non-selected workers come after selected
    }
    
    // If selection status is the same, sort alphabetically by name
    return workerA.FullName.localeCompare(workerB.FullName);
  });
  
  // Map over the sorted array
  return sortedWorkers.map(worker => {
    const isSelected = isWorkerAssigned(worker);
    
    return {
      id: worker.WorkerId,
      text: getWorkerDisplayName(worker),
      selected: isSelected,
      onClick: (e) => {
        e.stopPropagation();
        onToggleWorker(worker.FullName);
        mutate('/aoo-api/Productions/get-machines-status-events');
      }
    };
  });
};

// Inspector menu items generator
export const useInspectorItems = (
  workers: any[],
  machineId: string,
  machineInspectors: any[],
  onToggleInspector: (workerName: string) => void
): SubMenuItem[] => {
  const { mutate } = useSWRConfig();
  if (!workers?.length) return [];
  
  // Helper function to check if a worker is an inspector
  const isInspector = (worker) => {
    return machineInspectors.some(insp => {
      if (typeof insp === 'object') {
        const inspName = insp.FullName || insp.fullName || insp.Name || insp.name;
        return inspName === worker.FullName;
      }
      return false;
    });
  };

  // Create a copy and sort: selected first, then alphabetically
  const sortedWorkers = [...workers].sort((workerA, workerB) => {
    const aIsSelected = isInspector(workerA);
    const bIsSelected = isInspector(workerB);

    if (aIsSelected && !bIsSelected) {
      return -1; // Selected items come first
    }
    if (!aIsSelected && bIsSelected) {
      return 1; // Non-selected items come after selected
    }
    // If selection status is the same, sort alphabetically by name
    return workerA.FullName.localeCompare(workerB.FullName);
  });

  // Map over the sorted array
  return sortedWorkers.map(worker => ({
    id: worker.WorkerId,
    text: getWorkerDisplayName(worker),
    selected: isInspector(worker),
    onClick: (e) => {
      e.stopPropagation();
      onToggleInspector(worker.FullName);
      mutate('/aoo-api/Productions/get-machines-status-events')
    }
  }));
};

// Reason menu items generator
export const useReasonItems = (
  reasons: any[], 
  eventId: string, 
  eventState: string,
  onClose: () => void
): SubMenuItem[] => {
  if (!reasons?.length) return [];
  
  return reasons.map(reason => ({
    id: reason.Id,
    text: reason.Name,
    icon: <DisplayIcon value={reason.Name} height={20} width={25} color={"#363636"} />,
    onClick: (e) => {
      e.stopPropagation();
      postAction(
        AOO_API_HOST + `/aoo-api/Productions/post-failure-reason`,
        {
          EventId: eventId,
          ReasonId: reason.Id,
          Status: eventState,
        }
      );
      onClose();
    }
  }));
};

// Fix days menu items generator
export const useFixItems = (
  hasDaysToFix: boolean,
  onOpenFixModal: () => void,
  onOpenRemoveModal: () => void,
  onClose: () => void
): SubMenuItem[] => {
  const items: SubMenuItem[] = [];
  
  items.push({
    id: 'set-fix',
    text: hasDaysToFix ? "Edit days to fix" : "Set days to fix",
    onClick: (e) => {
      e.stopPropagation();
      onOpenFixModal();
      onClose();
    }
  });
  
  if (hasDaysToFix) {
    items.push({
      id: 'remove-fix',
      text: "Remove days",
      onClick: (e) => {
        e.stopPropagation();
        onOpenRemoveModal();
        onClose();
      }
    });
  }

  return items;
};

// Cover picture menu items generator
export const useCoverItems = (
  hasCover: boolean,
  machineId: string,
  onOpenCoverModal: () => void,
  onClose: () => void
): SubMenuItem[] => {
  const items: SubMenuItem[] = [];
  
  items.push({
    id: 'edit-cover',
    text: hasCover ? "Edit cover picture" : "Add cover picture",
    onClick: (e) => {
      e.stopPropagation();
      onOpenCoverModal();
      onClose();
    }
  });
  
  if (hasCover) {
    items.push({
      id: 'remove-cover',
      text: "Remove cover picture",
      onClick: (e) => {
        e.stopPropagation();
        postQueryAction("RemoveCoverPicture?MachineId", machineId);
        onClose();
      }
    });
  }

  return items;
}; 
